import { AccountType } from './types'
import { loadAccounts } from './accounts'
import { getTradeDates } from './api'
import isEmail from 'validator/es/lib/isEmail'

type FieldTypeInput = 'text' | 'number' | 'checkbox' | 'slot' | 'date'
type FieldTypeSelectable = 'select' | 'radio'
type FieldType = FieldTypeInput | FieldTypeSelectable | 'checkbox'

export interface IFieldSelectOption {
  title: string
  value: string | boolean
}

export type FieldSelectOptions = IFieldSelectOption[] | string[]
export type FieldSelectOptionsProvider = FieldSelectOptions | (() => Promise<FieldSelectOptions> | FieldSelectOptions | Ref<FieldSelectOptions>)
export type FormFieldBasic = FormFieldStandard | FormFieldSelectable
export type FormField = FormFieldBasic & IFormField

/** Checkbox 必須勾選；其他 required 欄位只需不是空值。 */
export function isRequiredFieldValueValid(field: FormFieldBasic | undefined, value: any): boolean {
  if (field?.type === 'checkbox') return value === true
  return value !== null && value !== undefined && value !== ''
}

export interface IFormFieldBasic {
  id: string
  required?: boolean
  hint?: string
  label?: string
  placeholder?: string
  type: FieldType
  selectOptions?: FieldSelectOptionsProvider
  default?: string
  rules?: Array<(value: any, form: Record<string, any>) => boolean | string>
  fields?: FormFieldBasic[]
  titleHint?: string
  /** 全部都為小寫 (只能搭配 type = text) */
  alllower?: boolean
  /** 全部都為大寫 (只能搭配 type = text) */
  allcaps?: boolean
  /** 只能輸入英數字 (只能搭配 type = text) */
  ascii?: false
  /** 字串長度 (只能搭配 type = text) */
  length?: number
  /** 貨幣格式 (只能搭配 type = text) */
  currency?: boolean
  /** 貨幣格式小數位數 (只能搭配 currency = true) */
  decimalDigits?: number
  /** 將輸入的資料同步到另一個欄位 */
  syncTo?: string
  min?: number | string
  max?: number | string
  /** (只能搭配 type = text), 預設 64 */
  maxLength?: number
  /** 多組數量 */
  multiple?: number
}

export interface IFormField extends IFormFieldBasic {
  name: string
  accountTypes: AccountType[]
}

export interface FormFieldStandard extends IFormFieldBasic {
  type: FieldTypeInput | 'slot'
}

export interface FormFieldSelectable extends IFormFieldBasic {
  type: FieldTypeSelectable
  selectOptions: FieldSelectOptionsProvider
}

/** 自然人帳戶類型；UG Joint 維持獨立，並在自然人資料欄位中明確加入。 */
const NaturalPerson = [AccountType.Personal, AccountType.Joint]
const Company = [AccountType.Nominee, AccountType.Institutional]
/** 所有帳戶類型，供電話、Email、郵寄地址等帳戶皆適用的欄位使用。 */
const Any = [...NaturalPerson, ...Company, AccountType.UGJoint]

export function isCompanyAccount(accountType?: AccountType | null) {
  return !!accountType && Company.includes(accountType)
}

/** Options: 國家選項 */
/** taiwan 排第一個，other 最後一個，其他的則是照字母 A-Z 排序 */
export const Countries: IFieldSelectOption[] = [
  { title: 'TAIWAN, REPUBLIC OF CHINA', value: 'TAIWAN, REPUBLIC OF CHINA' },
  { title: 'AUSTRALIA', value: 'AUSTRALIA' },
  { title: 'BAHAMAS', value: 'BAHAMAS' },
  { title: 'BELIZE', value: 'BELIZE' },
  { title: 'BRITISH VIRGIN ISLANDS', value: 'VIRGIN ISLANDS,BRITISH' },
  { title: 'CANADA', value: 'CANADA' },
  { title: 'CAYMAN ISLANDS', value: 'CAYMAN ISLANDS' },
  { title: 'HONG KONG', value: 'HONG KONG' },
  { title: 'IRELAND', value: 'IRELAND' },
  { title: 'JERSEY', value: 'JERSEY' },
  { title: 'LIECHTENSTEIN', value: 'LIECHTENSTEIN' },
  { title: 'LUXEMBOURG', value: 'LUXEMBOURG' },
  { title: 'MACAO', value: 'MACAO' },
  { title: 'SAINT VINCENT AND THE GRENADINES', value: 'SAINT VINCENT AND THE GRENADINES' },
  { title: 'SAMOA', value: 'SAMOA' },
  { title: 'SINGAPORE', value: 'SINGAPORE' },
  { title: 'SWITZERLAND', value: 'SWITZERLAND' },
]

/** Options: 擴充國家選項 */
const CountriesPlus: IFieldSelectOption[] = [
  ...Countries,
  { title: 'Other', value: 'Other' }
]

/** Options: 國家選項(額外增加 CHINA) */
export const CountriesWithChina: IFieldSelectOption[] = [
  { title: 'TAIWAN, REPUBLIC OF CHINA', value: 'TAIWAN, REPUBLIC OF CHINA' },
  { title: 'AUSTRALIA', value: 'AUSTRALIA' },
  { title: 'BAHAMAS', value: 'BAHAMAS' },
  { title: 'BELIZE', value: 'BELIZE' },
  { title: 'BRITISH VIRGIN ISLANDS', value: 'VIRGIN ISLANDS,BRITISH' },
  { title: 'CANADA', value: 'CANADA' },
  { title: 'CAYMAN ISLANDS', value: 'CAYMAN ISLANDS' },
  { title: 'CHINA', value: 'CHINA' },
  { title: 'HONG KONG', value: 'HONG KONG' },
  { title: 'IRELAND', value: 'IRELAND' },
  { title: 'JERSEY', value: 'JERSEY' },
  { title: 'LIECHTENSTEIN', value: 'LIECHTENSTEIN' },
  { title: 'LUXEMBOURG', value: 'LUXEMBOURG' },
  { title: 'MACAO', value: 'MACAO' },
  { title: 'SAINT VINCENT AND THE GRENADINES', value: 'SAINT VINCENT AND THE GRENADINES' },
  { title: 'SAMOA', value: 'SAMOA' },
  { title: 'SINGAPORE', value: 'SINGAPORE' },
  { title: 'SWITZERLAND', value: 'SWITZERLAND' },
  { title: 'Other', value: 'Other' }
]

/** 舊資料對應用 */
export const CountriesMigration: { [key: string]: string } = {
  "TW, TAIWAN,REPUBLIC OF CHINA": "TAIWAN, REPUBLIC OF CHINA",
  "AU, AUSTRALIA": "AUSTRALIA",
  "BS, BAHAMAS": "BAHAMAS",
  "BZ, BELIZE": "BELIZE",
  "CA, CANADA": "CANADA",
  "KY, CAYMAN ISLANDS": "CAYMAN ISLANDS",
  "HK, HONG KONG": "HONG KONG",
  "JE, JERSEY": "JERSEY",
  "VC, SAINT VINCENT AND THE GRENADINES": "SAINT VINCENT AND THE GRENADINES",
  "WS, SAMOA": "SAMOA",
  "SG, SINGAPORE": "SINGAPORE",
  "CH, SWITZERLAND": "SWITZERLAND",
  "VG, VIRGIN ISLANDS,BRITISH": "VIRGIN ISLANDS,BRITISH",
  "LU, LUXEMBOURG": "LUXEMBOURG",
  "LI, LIECHTENSTEIN": "LIECHTENSTEIN"
}

export function getCountryCorrection(country: string) {
  const corrected = CountriesMigration[country]
  return corrected ?? country
}

export function isCountryEqual(a: string, b: string) {
  return a === b || CountriesMigration[a] === b || CountriesMigration[b] === a
}

export function isCountryTaiwan(country?: string | null) {
  return country === 'TAIWAN, REPUBLIC OF CHINA' || country === 'TW, TAIWAN,REPUBLIC OF CHINA'
}

/** 稅籍號碼不適用的稅務繳納地。 */
const TaxIdUnavailableCountries = [
  'CAYMAN ISLANDS',
  'BAHAMAS',
  'BELIZE',
  'VIRGIN ISLANDS,BRITISH',
  'MACAO',
  'SAINT VINCENT AND THE GRENADINES',
  'SAMOA',
]

/**
 * 將稅務繳納地正規化，讓舊資料代碼與大小寫差異共用同一套判斷。
 * @param country 稅務繳納地代碼或名稱。
 * @returns 正規化後的大寫國家名稱；未提供時回傳空字串。
 */
export function getTaxPaymentCountry(country?: string | null) {
  return country ? getCountryCorrection(country)?.toUpperCase() : ''
}

/**
 * 判斷稅務繳納地是否可用 Reason A/B 取代 TIN。
 * @param country 稅務繳納地代碼或名稱。
 * @param accountType 帳戶類型，用於判斷列支敦斯登法人帳戶例外。
 * @returns 可選擇 Reason A/B 時回傳 true。
 */
export function canUseNoTinReason(country?: string | null, accountType?: AccountType | null) {
  const taxCountry = getTaxPaymentCountry(country)
  if (!taxCountry) return false
  if (TaxIdUnavailableCountries.includes(taxCountry)) return true
  return taxCountry === 'LIECHTENSTEIN' && isCompanyAccount(accountType)
}

/**
 * 判斷稅務繳納地是否必填 TIN；必填者不可選 Reason A/B。
 * @param country 稅務繳納地代碼或名稱。
 * @param accountType 帳戶類型。
 * @returns 已選擇國家且不能使用 Reason A/B 時回傳 true。
 */
export function isTaxIdCountryRequired(country?: string | null, accountType?: AccountType | null) {
  return !!getTaxPaymentCountry(country) && !canUseNoTinReason(country, accountType)
}

/** 是否顯示稅籍號碼欄位；N/A 國家不顯示舊版單欄 TIN。 */
export function showTaxIdNumber(profile: Record<string, any>, accountType?: AccountType | null) {
  return isTaxIdCountryRequired(profile.tax_payment_location, accountType)
}

/** 是否必填稅籍號碼。 */
export function isTaxIdNumberRequired(profile: Record<string, any>, accountType?: AccountType | null) {
  return isTaxIdCountryRequired(profile.tax_payment_location, accountType)
}

/**
 * Options: Self-Certification.tax_ref
 * 國籍對應的稅務地
 */
export const TaxRefNameFromCountry: Record<string, string> = {
  'TAIWAN, REPUBLIC OF CHINA': 'Taiwan ID',
  'AUSTRALIA': 'Tax number',
  'CANADA': 'Social Insurance No'
}

/** Partial: 申購 */
const FieldsSubscribe: FormField[] = [
  {
    id: 'client_name',
    name: '認購人名稱',
    rules: [
      v => (v !== null && v !== undefined) || '此欄位必填',
    ],
    required: true,
    type: 'slot',
    selectOptions: [
      { title: '新客戶', value: '*' },
    ],
    accountTypes: Any,
  },
  {
    id: 'account_type',
    name: '帳戶類型',
    required: true,
    type: 'select',
    selectOptions: [
      { title: '個人投資戶', value: AccountType.Personal },
      { title: '聯名戶', value: AccountType.Joint },
      { title: '機構投資者', value: AccountType.Institutional },
    ],
    accountTypes: Any,
    placeholder: '請選擇帳戶類型',
  },
  {
    id: 'subscribe_date',
    name: '認購交易日',
    required: true,
    type: 'select',
    selectOptions: () => getTradeDates('SBS'),
    accountTypes: Any,
    placeholder: '請選擇認購交易日',
  },
  {
    id: 'subscribe_fund',
    name: '認購基金',
    required: true,
    type: 'slot',
    accountTypes: Any,
    placeholder: '請選擇認購基金',
  },
  {
    id: 'subscribe_amount',
    name: '淨投資金額',
    required: true,
    type: 'number',
    accountTypes: Any,
    titleHint: '(USD)',
    placeholder: '淨投資金額',
    currency: true,
  },
  {
    id: 'subscribe_fee',
    name: '認購手續費',
    required: true,
    type: 'number',
    accountTypes: Any,
    titleHint: '(USD)',
    placeholder: '認購手續費',
    currency: true,
  },
  {
    id: 'subscribe_fee_rate',
    name: '認購手續費率',
    required: true,
    type: 'number',
    accountTypes: Any,
    titleHint: '(%)',
    placeholder: '認購手續費率',
    currency: true,
    decimalDigits: 1,
    rules: [
      v => (v !== null && v !== undefined) || '此欄位必填',
      v => (v <= 5) || '費率上限5%',
    ],
  },
  {
    id: 'china_beneficiary_count',
    name: '中國籍人數',
    required: true,
    type: 'number',
    accountTypes: Any,
    placeholder: '中國籍人數',
    currency: true,
    decimalDigits: 0,
    min: 0,
    rules: [
      v => (v !== null && v !== undefined) || '此欄位必填',
    ],
  },
]

export const Phone: { [name in 'CountryCode' | 'AreaCode' | 'PhoneNumber']: FormFieldBasic } = {
  CountryCode: {
    id: 'country_code',
    type: 'text',
    label: '國碼',
    maxLength: 8,
  },
  AreaCode: {
    id: 'area_code',
    type: 'text',
    label: '區碼',
    maxLength: 8,
  },
  PhoneNumber: {
    id: 'phone_number',
    type: 'text',
    label: '電話號碼',
    maxLength: 32,
  }
}

/** 子欄位組合：電話 */
export const PhoneFields: FormFieldBasic[] = [
  Phone.CountryCode,
  Phone.AreaCode,
  Phone.PhoneNumber,
]

/** 子欄位組合：地址 */
const AddressFields: FormFieldBasic[] = [
  {
    id: 'street',
    type: 'text',
    label: '街區門牌號',
    maxLength: 250,
  },
  {
    id: 'city',
    type: 'text',
    label: '鄉鎮市',
    required: false,
    maxLength: 32,
  },
  {
    id: 'county',
    type: 'text',
    label: '縣/省/州',
    required: false,
    maxLength: 32,
  },
  {
    id: 'postal_code',
    type: 'text',
    label: '郵遞區號',
    required: false,
    maxLength: 16,
  },
  {
    id: 'country',
    type: 'text',
    label: '國家',
    maxLength: 32,
    allcaps: true,
  },
  {
    // 虛擬欄位，僅供前端介面顯示
    id: 'countrySelection',
    type: 'select',
    label: '國家',
    selectOptions: CountriesPlus,
    placeholder: '請選擇國家',
  },
  {
    // 虛擬欄位，僅供前端介面顯示(增加 china 供郵寄地址使用)
    id: 'countrySelectionWithChina',
    type: 'select',
    label: '國家',
    selectOptions: CountriesWithChina,
    placeholder: '請選擇國家',
  }
]

/** Partial: 贖回 */
const FieldsRedeem: FormField[] = [
  {
    id: 'redeem_date',
    name: '贖回交易日',
    required: true,
    type: 'select',
    selectOptions: () => getTradeDates('RDM'),
    accountTypes: Any,
    placeholder: '請選擇贖回交易日',
  },
  {
    id: 'redeem_fund',
    name: '贖回基金',
    required: true,
    type: 'slot',
    accountTypes: Any,
  },
  {
    id: 'redeem_form',
    name: '贖回清單',
    required: true,
    type: 'slot',
    accountTypes: Any,
  },
  {
    id: 'redeem_method',
    name: '贖回股份數目/金額',
    required: true,
    type: 'radio',
    selectOptions: [
      { title: '股份數目', value: 'Shares' },
      { title: '金額(USD)', value: 'Amount' },
    ],
    accountTypes: Any,
    fields: [
      {
        id: 'redeem_amount',
        type: 'number',
        currency: true,
        rules: [
          (v) => +v > 0 || '此欄位必填'
        ]
      },
      {
        id: 'redeem_shares',
        type: 'number',
        currency: true,
        rules: [
          (v) => +v > 0 || '此欄位必填'
        ]
      },
    ],
  },
]

/** Partial: 轉換 */
const FieldsSwitch: FormField[] = [
  {
    id: 'switch_from',
    name: '贖回基金',
    required: true,
    type: 'slot',
    accountTypes: Any,
  },
  {
    id: 'switch_to',
    name: '認購基金',
    required: true,
    type: 'slot',
    accountTypes: Any,
    placeholder: '請選擇認購基金',
  },
]

/** Partial: 個人資料 */
const FieldsPersonal: FormField[] = [
  {
    name: '新認購人名稱',
    id: 'personal_name',
    required: true,
    type: 'slot',
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    fields: [
      {
        id: 'last_name',
        type: 'text',
        hint: '格式參考: Wang',
        placeholder: '姓',
      },
      {
        id: 'first_name',
        type: 'text',
        hint: '格式參考: Yang-Ming',
        placeholder: '名',
      },
    ]
  },
  {
    name: '出生地',
    id: 'birthplace',
    required: true,
    type: 'text',
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    placeholder: '出生地',
    hint: '請參照護照記載之出生地',
  },
  {
    name: '出生日期',
    id: 'date_of_birth',
    required: true,
    type: 'date',
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    placeholder: '出生日期',
  },
  {
    name: '國籍',
    id: 'nationality',
    required: true,
    type: 'select',
    selectOptions: CountriesWithChina,
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    placeholder: '請選擇國家',
  },
  {
    name: '護照簽發地',
    id: 'passport_issuing_place',
    required: true,
    type: 'select',
    selectOptions: CountriesWithChina,
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    placeholder: '請選擇國家',
  },
  {
    name: '護照號碼',
    id: 'passport_number',
    required: true,
    type: 'text',
    maxLength: 32,
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    placeholder: '護照號碼',
    rules: [
      (value, form) => {
        if (form.passport_issuing_place === 'TAIWAN, REPUBLIC OF CHINA') return !!String(value).match(/^\d{9}$/) || '護照號碼不正確'
        else return true
      }
    ]
  },
  {
    name: '稅務繳納地',
    id: 'tax_payment_location',
    required: true,
    type: 'select',
    selectOptions: CountriesWithChina,
    accountTypes: Any,
    placeholder: '請選擇國家',
  },
  {
    name: '稅籍號碼',
    id: 'tax_id_number',
    type: 'text',
    accountTypes: Any,
    placeholder: '稅籍號碼',
  },
  {
    name: '無稅籍號碼原因',
    id: 'no_tin_reason',
    type: 'text',
    accountTypes: Any,
  },
  {
    name: '無稅籍號碼說明',
    id: 'no_tin_explain',
    type: 'text',
    accountTypes: Any,
  },
  {
    name: '第二國籍',
    id: 'second_nationality',
    type: 'select',
    selectOptions: CountriesWithChina,
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    titleHint: '(如適用)',
    placeholder: '請選擇國家',
  },
  {
    name: '第二稅籍號碼',
    id: 'second_tax_id_number',
    type: 'text',
    accountTypes: Any,
    placeholder: '稅籍號碼',
  },
  {
    name: '第二無稅籍號碼原因',
    id: 'second_no_tin_reason',
    type: 'text',
    accountTypes: Any,
  },
  {
    name: '第二無稅籍號碼說明',
    id: 'second_no_tin_explain',
    type: 'text',
    accountTypes: Any,
  },
  {
    name: '第二稅務繳納地',
    hint: '無資料請填入N/A',
    id: 'second_tax_payment_location',
    type: 'select',
    selectOptions: CountriesWithChina,
    accountTypes: Any,
    titleHint: '(如適用)',
    placeholder: '請選擇國家',
  },
  // {
  //   name: '資金來源',
  //   id: 'source_of_funds',
  //   required: true,
  //   type: 'select',
  //   accountTypes: NaturalPerson,
  //   selectOptions: [
  //     { title: 'Sale of Existing Assets', value: 'Sale of Existing Assets' },
  //     { title: 'Inheritance', value: 'Inheritance' },
  //     { title: 'Salary/Bonus', value: 'Salary/Bonus' },
  //     { title: 'Other', value: 'Other' },
  //   ],
  // },
  // {
  //   name: '職業',
  //   id: 'occupation',
  //   required: true,
  //   type: 'text',
  //   accountTypes: NaturalPerson,
  //   placeholder: '職業',
  // },
  {
    name: '聯名戶彼此關係',
    id: 'shareholder_relationship',
    required: true,
    type: 'text',
    accountTypes: [AccountType.Joint],
    placeholder: '聯名戶彼此關係',
  },
]

/** Partial: 個人資料（公司） */
const FieldsBusiness: FormField[] = [
  {
    name: '公司名稱',
    id: 'business_name',
    required: true,
    type: 'text',
    accountTypes: Company,
  },
  {
    name: '公司註冊地',
    id: 'business_location',
    required: true,
    type: 'select',
    selectOptions: CountriesWithChina,
    accountTypes: Company,
  },
  {
    name: '公司登記證號碼',
    id: 'business_id_number',
    required: true,
    type: 'text',
    accountTypes: Company,
    // syncTo: 'tax_id_number'
  },
  // 稅務繳納地
  // 稅籍號碼
]

/** Partial: 銀行帳戶 */
const FieldsContact: FormField[] = [
  {
    id: 'phone_number',
    name: '電話號碼',
    required: true,
    type: 'slot',
    accountTypes: Any,
    fields: PhoneFields,
    multiple: 3,
  },
  {
    id: 'fax_number',
    name: '傳真號碼',
    required: false,
    type: 'slot',
    accountTypes: Any,
    fields: PhoneFields,
    multiple: 2,
  },
  {
    id: 'email',
    name: '電子郵件',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '電子郵件',
    maxLength: 100,
    rules: [str => (!str || isEmail(str)) || 'Email 格式錯誤'],
    multiple: 3,
    alllower: true,
  },
  {
    id: 'residence_address',
    name: '永久居住地址',
    required: true,
    type: 'slot',
    accountTypes: [...NaturalPerson, AccountType.UGJoint],
    fields: AddressFields,
  },
  {
    id: 'company_address',
    name: '現居地址或公司註冊地址',
    required: true,
    type: 'slot',
    accountTypes: Company,
    fields: AddressFields,
  },
  {
    id: 'mailing_address',
    name: '郵寄地址',
    required: true,
    type: 'slot',
    accountTypes: Any,
    fields: AddressFields,
  },
]

/** Partial: 銀行帳戶 */
const FieldsBankAccount: FormField[] = [
  {
    id: 'bank_swift_code',
    name: '銀行國際代碼',
    required: true,
    type: 'text',
    accountTypes: Any,
    placeholder: '銀行國際代碼',
    allcaps: true,
  },
  {
    id: 'bank_name',
    name: '銀行名稱',
    required: true,
    type: 'text',
    accountTypes: Any,
    placeholder: '銀行名稱',
    allcaps: true,
  },
  {
    id: 'bank_address',
    name: '銀行地址',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '銀行地址',
  },
  {
    id: 'bank_account_name',
    name: '客戶銀行帳戶名稱',
    required: true,
    type: 'text',
    accountTypes: Any,
    placeholder: '客戶銀行帳戶名稱',
  },
  {
    id: 'bank_account_number',
    name: '客戶銀行帳號',
    required: true,
    type: 'text',
    accountTypes: Any,
    placeholder: '客戶銀行帳號',
  },
  {
    id: 'intermediary_bank_name',
    name: '中間行名稱',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '中間行名稱',
    allcaps: true,
  },
  {
    id: 'intermediary_bank_swift_code',
    name: '中間行國際代碼',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '中間行國際代碼',
    allcaps: true,
    maxLength: 150,
  },
  {
    id: 'beneficiary_bank_account_number',
    name: '收款銀行帳號',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '收款銀行帳號',
  },
  {
    id: 'second_intermediary_bank_account_name',
    name: '第二中間行帳戶名稱',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '第二中間行帳戶名稱',
    allcaps: true,
  },
  {
    id: 'second_intermediary_bank_account_number',
    name: '第二中間行帳號',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '第二中間行帳號',
  },
  {
    id: 'bank_remark',
    name: '備註',
    required: false,
    type: 'text',
    accountTypes: Any,
    placeholder: '備註',
    maxLength: 150,
  },
  {
    id: 'bank_account_type',
    name: '此銀行帳戶為',
    required: true,
    type: 'radio',
    selectOptions: [
      { title: '單名帳戶', value: 'Individual Account' },
      { title: '聯名帳戶', value: 'Joint Account' },
    ],
    accountTypes: Any,
  },
  // 只有聯名戶可選擇銀行帳戶類型，且當銀行帳戶選擇「聯名戶」時才顯示此區塊
  {
    id: 'bank_joint_data_match',
    name: '此銀行帳戶名稱與認購人名稱及人數完全相符',
    required: true,
    type: 'radio',
    selectOptions: [
      { title: '是', value: true },
      { title: '否', value: false },
    ],
    accountTypes: [AccountType.Joint],
  },
  {
    id: 'bank_account_change_update',
    name: '變更銀行帳戶資料',
    required: true,
    type: 'radio',
    selectOptions: [
      { title: '限本次交易', value: false },
      { title: '套用到後續交易', value: true },
    ],
    accountTypes: Any,
  },
  {
    id: 'bank_account_user_confirmed',
    name: '本人/我等確認上述銀行帳戶資料之完整性與正確性',
    type: 'checkbox',
    required: true,
    accountTypes: Any,
  },
]

/** 所有欄位 */
export const FormFields: FormField[] = [
  ...FieldsSubscribe,
  ...FieldsRedeem,
  ...FieldsSwitch,
  ...FieldsPersonal,
  ...FieldsBusiness,
  ...FieldsContact,
  ...FieldsBankAccount,
]

/** 依據帳戶類別取得欄位定義 */
export function getField(id: string, accountType: AccountType) {
  return FormFields.find(f => f.id === id && f.accountTypes?.includes(accountType))
}
